import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Handshake, Building2, Wrench, Users, Truck, Award, BadgeCheck, CheckCircle2 } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import usePageContent from '../hooks/usePageContent';
import { PartnerMapSection } from './PartnerLocations';

const PARTNERS = [
    { key: 'industrial-contractors', icon: Wrench, name: 'Operators & Contractors', desc: 'Run Dry Ice Bulls equipment on your jobs and get leads in your territory.' },
    { key: 'equipment-dealers', icon: Truck, name: 'Equipment Dealers', desc: 'Sell our machines and accessories through your network with partner pricing.' },
    { key: 'rental-companies', icon: Building2, name: 'Rental Companies', desc: 'Add dry ice cleaning equipment to your rental fleet with training included.' },
    { key: 'referral-partners', icon: Users, name: 'Referral Partners', desc: 'Earn commission on qualified equipment referrals.' },
    { key: 'dry-ice-suppliers', icon: Award, name: 'Dry Ice Suppliers', desc: 'Become a preferred regional CO₂ / dry ice supply partner.' },
    { key: 'sales-representatives', icon: Handshake, name: 'Sales Representatives', desc: 'Independent reps with industrial relationships — commission available.' },
];

const track = (name, params = {}) => window.trackConversion && window.trackConversion(name, params);

const Section = ({ eyebrow, title, children, className = '', testid }) => (
    <section className={`border-t border-neutral-200 ${className}`} data-testid={testid}>
        <div className="container-page py-20 md:py-24">
            <div className="label-caps text-brand-primary mb-3">{eyebrow}</div>
            <h2 className="font-display font-black uppercase text-3xl md:text-5xl text-neutral-900 mb-10">{title}</h2>
            {children}
        </div>
    </section>
);

const Partners = () => {
    const content = usePageContent();
    useSEO({
        title: 'Partner Program — Authorized Dry Ice Bulls Partners',
        description: 'Become an authorized Dry Ice Bulls partner: partner pricing, certification, leads in your area and a listing on our national partner map.',
    });

    return (
        <div data-testid="partners-page">
            <section className="container-page pt-32 pb-20">
                <div className="label-caps text-brand-primary mb-4">/ Partner Program</div>
                <h1 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.95] text-neutral-900">
                    Become an<br /><span className="text-brand-primary">authorized</span> partner.
                </h1>
                <p className="mt-6 max-w-3xl text-base md:text-lg text-neutral-600 leading-relaxed" data-testid="partners-intro">{content?.partner_intro}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/partners/apply" onClick={() => track('partner_apply_click', { location: 'partners_hero' })}
                        className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-7 py-4 label-caps transition-colors" data-testid="partners-hero-apply">
                        Apply now <ArrowRight className="h-4 w-4" />
                    </Link>
                    <a href="#partner-map" className="inline-flex items-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-7 py-4 label-caps transition-colors" data-testid="partners-hero-map">See the network</a>
                </div>
            </section>

            <Section eyebrow="/ 01 — Benefits" title="What partners get" className="bg-neutral-50" testid="partners-benefits">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
                    {(content?.partner_benefits || []).map((b, i) => (
                        <div key={i} className="bg-white p-8" data-testid={`partner-benefit-${i}`}>
                            <BadgeCheck className="h-6 w-6 text-brand-primary" />
                            <div className="mt-4 font-display font-bold uppercase text-xl text-neutral-900">{b.title}</div>
                            <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{b.text}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <Section eyebrow="/ 02 — Requirements" title="What we ask of partners" testid="partners-requirements">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
                    {(content?.partner_requirements || []).map((r, i) => (
                        <li key={i} className="flex gap-3 border border-neutral-200 p-5 text-neutral-700" data-testid={`partner-requirement-${i}`}>
                            <CheckCircle2 className="h-5 w-5 text-brand-primary flex-shrink-0 mt-0.5" />{r}
                        </li>
                    ))}
                </ul>
            </Section>

            <Section eyebrow="/ 03 — Partner tracks" title="Pick your track" className="bg-neutral-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {PARTNERS.map(({ key, icon: Icon, name, desc }) => (
                        <Link key={key} to={`/partners/apply?program=${key}`} onClick={() => track('partner_program_click', { program: key, location: 'partners_grid' })}
                            className="group border border-neutral-200 bg-white p-6 hover:border-brand-primary transition-colors flex flex-col" data-testid={`partner-${key}`}>
                            <Icon className="h-6 w-6 text-brand-primary" />
                            <div className="mt-4 font-display font-bold uppercase text-xl text-neutral-900 group-hover:text-brand-primary transition-colors">{name}</div>
                            <p className="mt-2 text-sm text-neutral-600 flex-1">{desc}</p>
                            <div className="mt-4 inline-flex items-center gap-1.5 label-caps text-brand-primary text-xs">Apply <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></div>
                        </Link>
                    ))}
                </div>
            </Section>

            <section className="border-t border-neutral-200" id="partner-map">
                <div className="container-page py-20 md:py-24">
                    <div className="label-caps text-brand-primary mb-3">/ 04 — The network</div>
                    <h2 className="font-display font-black uppercase text-3xl md:text-5xl text-neutral-900 mb-8">Partners across the US</h2>
                    <PartnerMapSection />
                </div>
            </section>
        </div>
    );
};

export default Partners;
